import React, {useCallback, useEffect, useState} from 'react';
import {
  GridRowClassNameParams,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid';
import {debounce} from 'lodash';
import AurDataGrid from '../../../common/aur-data-grid/AurDataGrid';
import {
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_50_ROW,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../../constants/constants';
import {
  convertToDecimalPlace,
  getTreeDataColumn,
} from '../../../../utils/inTakeAuditReportUtils';
import {CustodianType} from '../ByCustodianTab';
import AurDataGridLoadingOverlay from '../../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {t} from 'i18next';
import {formateNumber} from '../../../../utils/utils';
import {Typography} from '@mui/material';
import {TotalCumulativeVolumeReportType} from '../intakeAuditTypes';

interface DeliveryReportDataTableProps {
  deliveryData: CustodianType[];
  totalCumulativeReceivedVolume: TotalCumulativeVolumeReportType;
  onDeliveryRowHoverEvent?: (event: CustodianType | null) => void; // callback function to send hovered row data to update on progress chart
  onPagePropsChange?: (itemsPerPage: number, currentPage: number) => void;
  totalRecordsCount?: number;
  onRefresh?: () => void;
  isChartFailed?: boolean;
  isChartLoading?: boolean;
  size?: number;
}

type ExtendedCustodianType = CustodianType & {
  children?: CustodianType[];
};

const DeliveryReportDataTable: React.FC<DeliveryReportDataTableProps> = ({
  deliveryData,
  totalCumulativeReceivedVolume,
  onDeliveryRowHoverEvent,
  totalRecordsCount = 0,
  onPagePropsChange,
  onRefresh,
  isChartFailed,
  isChartLoading,
  size,
}) => {
  const gridAPIRef = useGridApiRef();
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_50_ROW
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const [lastChildMap, setLastChildMap] = useState<Record<string, string>>({});
  const [isFetchDeliveryDataInitial, setIsFetchDeliveryDataInitial] =
    useState(true);

  useEffect(() => {
    if (!isFetchDeliveryDataInitial) {
      onPagePropsChange ? onPagePropsChange(itemsPerPage, currentPage) : '';
    }
    setExpandedRowIds(new Set());
    setLastChildMap({});
    debounceHoverEvent(null);
  }, [itemsPerPage, currentPage]);

  useEffect(() => {
    if (
      !isFetchDeliveryDataInitial &&
      totalRecordsCount < TABLE_PER_PAGE_DEFAULT_50_ROW
    ) {
      setItemsPerPage(TABLE_PER_PAGE_DEFAULT_NO_ROW);
    }
    setIsFetchDeliveryDataInitial(false);
  }, [totalRecordsCount]);

  const totalRow: {[key: string]: number | string} = {
    expand: t('reports.intakeAudit.custodianTable.totals'),
    emailCompressed: totalCumulativeReceivedVolume.email,
    externallyProcessed: totalCumulativeReceivedVolume.externallyProcessed,
    otherData: totalCumulativeReceivedVolume.otherData,
    subTotal: 0,
    id: 'totals-row',
    receivedDate: '',
  };

  const renderTableCellData = (
    value: string | number,
    textStyle = 'body3_size row-cell-text'
  ) => {
    const data = typeof value === 'number' ? formateNumber(value) : value;
    return <Typography className={`typo-text ${textStyle}`}>{data}</Typography>;
  };

  const renderTotalRowComponent = useCallback(() => {
    return (
      <div className="totals-row-container" data-testid="total-delivery-row">
        {getTreeDataColumn()?.map((column, index) => (
          <div
            style={{width: `${(column.flex ?? 0) * 100}%`}}
            className={'table-data-cell'}
            key={`${column.field}-${index}`}
            data-testid={`${column?.field}-total`}
          >
            {renderTableCellData(
              column.field === 'expand'
                ? totalRow?.[column?.field as string]
                : column.field === 'deliveryDetails' ||
                    column.field === 'evidenceDetails'
                  ? ''
                  : convertToDecimalPlace(
                      totalRow?.[column?.field as string].toString()
                    ),
              'label1_size text-color'
            )}
          </div>
        ))}
      </div>
    );
  }, [totalRow]);

  // debounce hover event of table row to send back to subscriber
  const debounceHoverEvent = useCallback(
    debounce((param: CustodianType | null) => {
      onDeliveryRowHoverEvent && onDeliveryRowHoverEvent(param);
    }, 300),
    [onDeliveryRowHoverEvent]
  );

  const handleFirstCellClick = useCallback(
    (event: GridRowParams) => {
      const clickedRowId = event.row.id;
      if (event.row?.parentId) return; // If child row or invalid -> skip

      const updatedExpandedRowIds = new Set(expandedRowIds);
      const updatedLastChildMap = {...lastChildMap};

      // Toggle expand/collapse
      if (expandedRowIds.has(clickedRowId)) {
        updatedExpandedRowIds.delete(clickedRowId);
        delete updatedLastChildMap[clickedRowId];
        debounceHoverEvent(null);
      } else {
        updatedExpandedRowIds.add(clickedRowId);

        const rowData = deliveryData.find(
          (row): row is ExtendedCustodianType => row.id === clickedRowId
        );

        if (rowData?.children?.length) {
          const lastChildId = rowData.children[rowData.children.length - 1].id;
          if (lastChildId) {
            updatedLastChildMap[clickedRowId] = lastChildId;
          }
          /**
           * When a row is collapsed, or when the first row is clicked while expanded,
           * ignore `event.row` because it does not contain the complete data.
           * Use `originalData` instead, as it contains the actual parent row data,
           * including the total count required for the progress bar.
           */
          // When row get collapsed or first row row click when its expanded then need to ingore event.row data as its not contain total data
          // OriginalData contain actual parent row data which showing total count and this need to show on progress bar
          debounceHoverEvent(event.row.originalData ?? event.row);
        }
      }
      setExpandedRowIds(updatedExpandedRowIds);
      setLastChildMap(updatedLastChildMap);
    },
    [expandedRowIds, lastChildMap, deliveryData]
  );

  const getInventoryRowClassName = (params: GridRowClassNameParams) => {
    const rowId = params.row.id;

    const isParentSelected = expandedRowIds.has(rowId);
    const isLastChildOfAnySelected =
      Object.values(lastChildMap).includes(rowId);

    let className = '';
    if (isParentSelected) className += 'selected-row';
    if (isLastChildOfAnySelected)
      className += (className ? ' ' : '') + 'selected-row-last-child-row';

    return className;
  };

  return (
    <AurDataGrid
      columns={getTreeDataColumn()}
      rows={deliveryData}
      pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
      checkBoxVisible={false}
      entityType={''}
      visibleToolTip={true}
      className="intakeAuditTableByDelivery"
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      itemsPerPage={itemsPerPage}
      setItemsPerPage={setItemsPerPage}
      isTreeView={true}
      columnHeaderHeight={64}
      apiRef={gridAPIRef}
      totalRowCount={totalRecordsCount}
      footerChildComponent={renderTotalRowComponent()}
      onRowClick={handleFirstCellClick}
      getRowClassName={getInventoryRowClassName}
      disableRowSelectionOnClick
      containerHeight={size}
      noRowsOverlay={
        isChartLoading
          ? AurDataGridLoadingOverlay
          : isChartFailed
            ? AurDataGridErrorOverlay
            : undefined
      }
      onRefresh={onRefresh}
      minItemForPagination={TABLE_PER_PAGE_DEFAULT_NO_ROW}
    />
  );
};

export default DeliveryReportDataTable;
